import {useLazyQuery} from "@apollo/client";
import {ForwardedRef, forwardRef} from "react";
import {SelectInstance} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectComponent, SelectProps} from "@/components/select/Select.types.ts";
import {useLoadOptions} from "@/lib/hooks/useLoadOptions.ts";
import {ContactGroupsBaseResult, GQL_CONTACT_GROUPS_BASIC_QUERY} from "@/lib/queries/contacts.ts";
import {ContactGroupBase, PENDING_CONTACT_ID} from "@/lib/types/contacts.ts";

type ContactOption = ContactGroupBase & {displayLabel?: string};

export const ContactsSelect = forwardRef(
  ({options, ...props}: SelectProps<ContactOption>, ref: ForwardedRef<SelectInstance<ContactOption>>) => {
    const [getContacts] = useLazyQuery<ContactGroupsBaseResult>(GQL_CONTACT_GROUPS_BASIC_QUERY, {
      variables: {
        limit: 100,
        offset: 0,
        sort: "name",
      },
    });

    const {options: contacts, isLoading} = useLoadOptions<ContactOption, ContactGroupsBaseResult>({
      options: options,
      fetchOptions: getContacts,
      transform: (result) => Array.from(result.data?.contactGroups?.items || []),
    });

    return (
      <Select
        isLoading={isLoading}
        getOptionLabel={(contact) => contact.displayLabel ?? contact.name}
        getOptionValue={(contact) =>
          contact.id === PENDING_CONTACT_ID ? `pending:${contact.name}` : `id:${contact.id}`
        }
        getNewOptionData={(inputValue) => ({
          id: PENDING_CONTACT_ID,
          name: inputValue,
          displayLabel: `${inputValue} - Add`,
        })}
        options={contacts}
        {...props}
        ref={ref}
      />
    );
  }
) as SelectComponent;
